import { useState, useMemo, useEffect } from 'react'
import {
  Container, Title, Stack, Card, Text, Group, Button, Loader, Center,
  Badge, SimpleGrid, Select, Textarea, ThemeIcon, Progress, Accordion,
  Table, ScrollArea, ActionIcon, Tabs,
} from '@mantine/core'
import {
  IconSend, IconArrowRight, IconChevronLeft, IconChevronRight,
  IconEye, IconChecklist, IconCircleCheck, IconClock, IconCheck,
  IconStack2, IconShieldCheck, IconUserCheck, IconX, IconList,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  useFeatureGroupApi,
  type SampleOffense,
  type FeatureGroup,
} from '@/hooks/useFeatureCombinationApi'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'
import type { ComplianceDashboardData } from '@/types'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()

const EXTERNAL_STATUS_COLORS: Record<string, string> = {
  PENDING_EXTERNAL: 'yellow',
  CONFIRMED: 'green',
  REJECTED: 'red',
  DISPUTED: 'red',
}

const EXTERNAL_STATUS_LABELS: Record<string, string> = {
  PENDING_EXTERNAL: 'Pending Review',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
  DISPUTED: 'Disputed',
}

export function ExternalQAPage() {
  const api = useFeatureGroupApi()
  const queryClient = useQueryClient()

  const [currentIndex, setCurrentIndex] = useState(0)
  const [verdict, setVerdict] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [selectedOffense, setSelectedOffense] = useState<ComplianceDashboardData | null>(null)
  const [offenseDialogOpen, setOffenseDialogOpen] = useState(false)
  const [skippedHashes, setSkippedHashes] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<string>('pending')

  const { data: groupsResponse, isLoading } = useQuery({
    queryKey: ['external-qa-groups'],
    queryFn: () => api.getGroups({
      review_status: 'APPROVED',
      exclusion_applied: false,
    }),
  })

  const allGroups = groupsResponse?.data ?? []

  const groups = useMemo(() =>
    allGroups.filter((g: FeatureGroup) =>
      g.dominant_queue === 'Automation'
      && !g.exclusion_applied
      && g.review_status === 'APPROVED'
    ),
    [allGroups],
  )

  const pendingGroups = useMemo(() =>
    groups.filter((g: FeatureGroup) =>
      (!g.external_review_status || g.external_review_status === 'PENDING_EXTERNAL')
      && !skippedHashes.has(g.group_hash)
    ),
    [groups, skippedHashes],
  )
  const confirmedGroups = useMemo(() =>
    groups.filter((g: FeatureGroup) => g.external_review_status === 'CONFIRMED'),
    [groups],
  )
  const disputedGroups = useMemo(() =>
    groups.filter((g: FeatureGroup) => g.external_review_status === 'DISPUTED' || g.external_review_status === 'REJECTED'),
    [groups],
  )

  const displayGroups = useMemo(() => {
    if (activeTab === 'confirmed') return confirmedGroups
    if (activeTab === 'disputed') return disputedGroups
    if (activeTab === 'all') return groups
    return pendingGroups
  }, [activeTab, pendingGroups, confirmedGroups, disputedGroups, groups])

  const currentGroup = displayGroups[currentIndex] || null

  const { data: sampleOffenses = [], isLoading: loadingOffenses } = useQuery({
    queryKey: ['external-qa-offenses', currentGroup?.group_hash],
    queryFn: () => api.getSampleOffenses(currentGroup!.group_hash, 10),
    enabled: !!currentGroup,
  })

  const UBS_EXCLUSION_PATTERN = /exclusion rule/i

  const hasUbsExclusion = useMemo(() =>
    sampleOffenses.some((o: SampleOffense) =>
      o.label_notes && UBS_EXCLUSION_PATTERN.test(o.label_notes)
    ),
    [sampleOffenses],
  )

  useEffect(() => {
    if (hasUbsExclusion && currentGroup && !loadingOffenses) {
      setSkippedHashes(prev => new Set(prev).add(currentGroup.group_hash))
    }
  }, [hasUbsExclusion, currentGroup?.group_hash, loadingOffenses])

  const exampleOffense = sampleOffenses[0] || null

  const externalReviewMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.submitExternalReview(id, data),
    onSuccess: () => {
      setSubmitted(true)
      queryClient.invalidateQueries({ queryKey: ['external-qa-groups'] })
    },
  })

  const handleSubmit = () => {
    if (!currentGroup || !verdict) return
    externalReviewMutation.mutate({
      id: currentGroup.group_hash,
      data: {
        external_qa_verdict: verdict,
        external_qa_notes: notes || undefined,
        external_review_status: verdict === 'AGREE' ? 'CONFIRMED' : 'DISPUTED',
      },
    })
  }

  const goToNext = () => {
    setSubmitted(false)
    setVerdict(null)
    setNotes('')
    if (currentIndex < displayGroups.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  const goToPrev = () => {
    setSubmitted(false)
    setVerdict(null)
    setNotes('')
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  if (isLoading && !groupsResponse) {
    return <Center h="60vh"><Loader size="lg" /></Center>
  }

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Group gap="xs" mb={4}>
            <ThemeIcon variant="light" color="violet" size="lg" radius="md"><IconUserCheck size={22} /></ThemeIcon>
            <Title order={2}>External QA Review</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Review internally-approved automation groups. Only groups with no exclusion filters are shown.
          </Text>
        </div>

        {/* Stats */}
        <SimpleGrid cols={{ base: 2, sm: 3 }}>
          <Card shadow="sm" p="md" radius="md" withBorder>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">Pending Review</Text>
            <Text size="xl" fw={700} mt={4} c="yellow.7">{fmt(pendingGroups.length)}</Text>
          </Card>
          <Card shadow="sm" p="md" radius="md" withBorder>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">Confirmed</Text>
            <Text size="xl" fw={700} mt={4} c="teal">{fmt(confirmedGroups.length)}</Text>
          </Card>
          <Card shadow="sm" p="md" radius="md" withBorder>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">Progress</Text>
            <Text size="xl" fw={700} mt={4} c="violet">
              {groups.length > 0 ? `${((confirmedGroups.length / groups.length) * 100).toFixed(0)}%` : '0%'}
            </Text>
            <Progress
              value={groups.length > 0 ? (confirmedGroups.length / groups.length) * 100 : 0}
              color="violet" size="sm" mt={6} radius="xl"
            />
          </Card>
        </SimpleGrid>

        {/* Filter Tabs */}
        <Tabs value={activeTab} onChange={(v) => { setActiveTab(v || 'pending'); setCurrentIndex(0); setSubmitted(false); setVerdict(null); setNotes('') }}>
          <Tabs.List>
            <Tabs.Tab value="pending" leftSection={<IconClock size={14} />}>Pending ({fmt(pendingGroups.length)})</Tabs.Tab>
            <Tabs.Tab value="confirmed" leftSection={<IconCheck size={14} />}>Confirmed ({fmt(confirmedGroups.length)})</Tabs.Tab>
            <Tabs.Tab value="disputed" leftSection={<IconX size={14} />}>Disputed ({fmt(disputedGroups.length)})</Tabs.Tab>
            <Tabs.Tab value="all" leftSection={<IconList size={14} />}>All ({fmt(groups.length)})</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* No groups in current tab */}
        {displayGroups.length === 0 ? (
          <Card shadow="sm" p="xl" radius="md" withBorder>
            <Center>
              <Stack align="center" gap="xs">
                <ThemeIcon variant="light" color="teal" size="xl" radius="xl"><IconCircleCheck size={28} /></ThemeIcon>
                <Title order={4} c="teal">{activeTab === 'pending' ? 'All Caught Up' : 'No Groups'}</Title>
                <Text c="dimmed" size="sm">
                  {activeTab === 'pending' ? 'No pending groups to review. Check back after the next pipeline run.' : `No ${activeTab} groups found.`}
                </Text>
              </Stack>
            </Center>
          </Card>
        ) : currentGroup && (
          <Card shadow="md" p="lg" radius="lg" withBorder>
            <Stack gap="md">
              {/* Navigation */}
              <Group justify="space-between">
                <ActionIcon variant="light" color="gray" size="lg" onClick={goToPrev} disabled={currentIndex === 0}>
                  <IconChevronLeft size={18} />
                </ActionIcon>
                <Text size="sm" fw={500} c="dimmed">
                  {currentIndex + 1} of {fmt(displayGroups.length)}
                </Text>
                <ActionIcon variant="light" color="gray" size="lg" onClick={goToNext} disabled={currentIndex >= displayGroups.length - 1}>
                  <IconChevronRight size={18} />
                </ActionIcon>
              </Group>

              {/* Group summary badges */}
              <Group gap="xs">
                {currentGroup.external_review_status && (
                  <Badge
                    color={EXTERNAL_STATUS_COLORS[currentGroup.external_review_status] || 'gray'}
                    variant="light" size="sm"
                  >
                    {EXTERNAL_STATUS_LABELS[currentGroup.external_review_status] || currentGroup.external_review_status}
                  </Badge>
                )}
              </Group>

              {/* Internal QA assessment */}
              {currentGroup.qa_notes && (
                <Card withBorder radius="md" p="sm" bg="gray.0">
                  <Group gap="xs" mb={4}>
                    <ThemeIcon variant="light" color="gray" size="sm"><IconShieldCheck size={14} /></ThemeIcon>
                    <Text size="xs" fw={600}>Internal QA Notes</Text>
                  </Group>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{currentGroup.qa_notes}</Text>
                </Card>
              )}

              {/* Sample record */}
              {loadingOffenses ? (
                <Center py="md"><Loader size="sm" /></Center>
              ) : exampleOffense ? (
                <>
                  <Card withBorder radius="md" p="md" bg="gray.0">
                    <Group gap="xs" mb="sm">
                      <ThemeIcon variant="light" color="blue" size="sm"><IconEye size={14} /></ThemeIcon>
                      <Text size="sm" fw={700}>Sample Record</Text>
                      <Text size="xs" c="dimmed">One offense from this group</Text>
                    </Group>
                    <SimpleGrid cols={3} spacing="xs" verticalSpacing={6}>
                      {[
                        { label: 'Search ID', value: exampleOffense.search_id },
                        { label: 'Offense ID', value: exampleOffense.offense_id },
                        { label: 'Case Number', value: exampleOffense.case_number },
                        { label: 'Charge', value: exampleOffense.charge },
                        { label: 'Charge Description', value: exampleOffense.charge_description },
                        { label: 'Disposition', value: exampleOffense.disposition },
                        { label: 'Offense Type', value: exampleOffense.offense_type },
                        { label: 'Offense Date', value: exampleOffense.offense_date },
                        { label: 'Arrest Date', value: exampleOffense.arrest_date },
                        { label: 'Disposition Date', value: exampleOffense.disposition_date },
                        { label: 'File Date', value: exampleOffense.file_date },
                        { label: 'Age at Offense', value: exampleOffense.age_at_offense },
                        { label: 'Offense Status', value: exampleOffense.offense_status },
                        { label: 'Jurisdiction', value: exampleOffense.jurisdiction },
                        { label: 'Jurisdiction State', value: exampleOffense.jurisdiction_state },
                      ].map((field) => {
                        let raw = field.value
                        if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
                          raw = (raw as any).value ?? JSON.stringify(raw)
                        }
                        const v = raw === null || raw === undefined || raw === '' ? 'N/A' : String(raw)
                        return (
                          <div key={field.label} style={{ padding: '4px 8px' }}>
                            <Text size="xs" c="dimmed">{field.label}</Text>
                            <Text size="sm" lineClamp={1}>{v}</Text>
                          </div>
                        )
                      })}
                    </SimpleGrid>
                  </Card>

                  {/* Decisions */}
                  <Card withBorder radius="md" p="md">
                    <Group gap="xs" mb="sm">
                      <ThemeIcon variant="light" color="grape" size="sm"><IconChecklist size={14} /></ThemeIcon>
                      <Text size="sm" fw={700}>Decisions</Text>
                    </Group>
                    <SimpleGrid cols={2} spacing="md">
                      <Card withBorder radius="sm" p="sm" bg="indigo.0" style={{ border: '1px solid var(--mantine-color-indigo-2)' }}>
                        <Text size="xs" fw={600} c="indigo.7" mb={4}>AI (Compliance Engine)</Text>
                        <Group gap="xs" mb={4}>
                          <Text size="xs" c="dimmed">Decision:</Text>
                          <Badge size="sm" variant="light" color="indigo">{exampleOffense.ce_decision || 'N/A'}</Badge>
                        </Group>
                        <div style={{ marginBottom: 4 }}>
                          <Text size="xs" c="dimmed">Rules Applied</Text>
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{exampleOffense.ce_rules_applied || 'N/A'}</Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">Rationale</Text>
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{exampleOffense.ce_rationale || 'N/A'}</Text>
                        </div>
                      </Card>
                      <Card withBorder radius="sm" p="sm" bg="teal.0" style={{ border: '1px solid var(--mantine-color-teal-2)' }}>
                        <Text size="xs" fw={600} c="teal.7" mb={4}>UBS (Ground Truth)</Text>
                        <Group gap="xs" mb={4}>
                          <Text size="xs" c="dimmed">Decision:</Text>
                          <Badge size="sm" variant="light" color="teal">{exampleOffense.ground_truth || 'N/A'}</Badge>
                        </Group>
                        <div>
                          <Text size="xs" c="dimmed">Notes</Text>
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{exampleOffense.label_notes || 'N/A'}</Text>
                        </div>
                      </Card>
                    </SimpleGrid>
                  </Card>
                </>
              ) : (
                <Center py="lg">
                  <Stack align="center" gap="xs">
                    <ThemeIcon variant="light" color="gray" size="xl" radius="xl"><IconStack2 size={24} /></ThemeIcon>
                    <Text c="dimmed" size="sm">No offenses found for this group</Text>
                  </Stack>
                </Center>
              )}

              {/* Related offenses */}
              <Accordion variant="separated" radius="md">
                <Accordion.Item value="related">
                  <Accordion.Control icon={<IconStack2 size={18} />}>
                    <Group gap="xs">
                      <Text size="sm" fw={600}>Related Offenses</Text>
                      <Badge size="sm" variant="light" color="gray">{sampleOffenses.length}</Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {loadingOffenses ? <Center py="md"><Loader size="sm" /></Center> : (
                      <ScrollArea h={300}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders>
                          <Table.Thead style={{ background: 'var(--mantine-color-gray-0)' }}>
                            <Table.Tr>
                              <Table.Th>Offense ID</Table.Th>
                              <Table.Th>Charge</Table.Th>
                              <Table.Th>Disposition</Table.Th>
                              <Table.Th>AI Decision</Table.Th>
                              <Table.Th>Ground Truth</Table.Th>
                              <Table.Th>Match</Table.Th>
                              <Table.Th>Confidence</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {sampleOffenses.map((o: SampleOffense) => (
                              <Table.Tr
                                key={o.offense_id}
                                onClick={() => { setSelectedOffense(o as unknown as ComplianceDashboardData); setOffenseDialogOpen(true) }}
                                style={{ cursor: 'pointer' }}
                              >
                                <Table.Td><Text size="xs" ff="monospace">{o.offense_id}</Text></Table.Td>
                                <Table.Td><Text size="xs" lineClamp={1}>{o.charge || '-'}</Text></Table.Td>
                                <Table.Td><Text size="xs" lineClamp={1}>{o.disposition || '-'}</Text></Table.Td>
                                <Table.Td><Text size="xs">{o.ce_decision || 'N/A'}</Text></Table.Td>
                                <Table.Td><Text size="xs">{o.ground_truth || 'N/A'}</Text></Table.Td>
                                <Table.Td>
                                  <Badge size="xs" variant="light" color={o.match_status === 'MATCH' ? 'teal' : o.match_status === 'MISMATCH' ? 'red' : 'gray'}>
                                    {o.match_status || '-'}
                                  </Badge>
                                </Table.Td>
                                <Table.Td><Text size="xs">{o.ce_confidence?.toFixed(2) ?? 'N/A'}</Text></Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>

              {/* Previous review summary — shown for confirmed/disputed */}
              {activeTab !== 'pending' && currentGroup.external_qa_verdict && (
                <Card withBorder radius="md" p="md" bg={currentGroup.external_review_status === 'CONFIRMED' ? 'teal.0' : 'red.0'}
                  style={{ border: `1px solid var(--mantine-color-${currentGroup.external_review_status === 'CONFIRMED' ? 'teal' : 'red'}-2)` }}>
                  <Group gap="xs" mb="sm">
                    <ThemeIcon variant="light" color={currentGroup.external_review_status === 'CONFIRMED' ? 'teal' : 'red'} size="sm">
                      {currentGroup.external_review_status === 'CONFIRMED' ? <IconCheck size={14} /> : <IconX size={14} />}
                    </ThemeIcon>
                    <Text size="sm" fw={700}>External Review</Text>
                    <Badge size="sm" variant="light" color={EXTERNAL_STATUS_COLORS[currentGroup.external_review_status || ''] || 'gray'}>
                      {EXTERNAL_STATUS_LABELS[currentGroup.external_review_status || ''] || currentGroup.external_review_status}
                    </Badge>
                  </Group>
                  <SimpleGrid cols={3} spacing="xs">
                    <div>
                      <Text size="xs" c="dimmed">Verdict</Text>
                      <Text size="sm" fw={500}>{currentGroup.external_qa_verdict}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Reviewer</Text>
                      <Text size="sm" fw={500}>{currentGroup.external_reviewed_by_name || currentGroup.external_reviewed_by_email || 'N/A'}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Reviewed At</Text>
                      <Text size="sm">{currentGroup.external_reviewed_at ? new Date(currentGroup.external_reviewed_at).toLocaleDateString() : 'N/A'}</Text>
                    </div>
                  </SimpleGrid>
                  {currentGroup.external_qa_notes && (
                    <div style={{ marginTop: 8 }}>
                      <Text size="xs" c="dimmed">Notes</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{currentGroup.external_qa_notes}</Text>
                    </div>
                  )}
                </Card>
              )}

              {/* Review form — only show for pending groups */}
              {activeTab === 'pending' && <Card withBorder radius="md" p="md" bg="violet.0" style={{ border: '1px solid var(--mantine-color-violet-2)' }}>
                <Group gap="xs" mb="sm">
                  <ThemeIcon variant="light" color="violet" size="sm"><IconSend size={14} /></ThemeIcon>
                  <Text size="sm" fw={700}>Your External Review</Text>
                </Group>
                <Stack gap="sm">
                  <Select
                    label="Verdict"
                    placeholder="Do you agree with the AI assessment?"
                    data={[
                      { value: 'AGREE', label: 'Agree — AI decision looks correct' },
                      { value: 'DISAGREE', label: 'Disagree — AI decision is wrong' },
                    ]}
                    value={verdict}
                    onChange={setVerdict}
                    clearable
                  />
                  <Textarea
                    label="Notes (optional)"
                    placeholder="Any observations or concerns..."
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                  />
                  <Group justify="flex-end">
                    {submitted ? (
                      <Button
                        rightSection={<IconArrowRight size={14} />}
                        onClick={goToNext}
                        variant="gradient"
                        gradient={{ from: 'teal', to: 'cyan' }}
                        disabled={currentIndex >= displayGroups.length - 1}
                      >
                        Next Group
                      </Button>
                    ) : (
                      <Button
                        leftSection={<IconSend size={14} />}
                        onClick={handleSubmit}
                        loading={externalReviewMutation.isPending}
                        disabled={!verdict}
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'violet' }}
                      >
                        Submit External Review
                      </Button>
                    )}
                  </Group>
                </Stack>
              </Card>}
            </Stack>
          </Card>
        )}
      </Stack>

      <OffenseDetailDialog
        opened={offenseDialogOpen}
        onClose={() => { setOffenseDialogOpen(false); setSelectedOffense(null) }}
        offense={selectedOffense}
        allOffensesInSearch={sampleOffenses.filter((o: SampleOffense) => o.search_id === selectedOffense?.search_id) as unknown as ComplianceDashboardData[]}
      />
    </Container>
  )
}
